import React from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Popup } from '@progress/kendo-react-popup';
import { DateRangePicker } from '@progress/kendo-react-dateinputs';
import { IntlProvider, LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import { ShowChart } from 'styled-icons/material';
import { DotsVerticalRounded } from 'styled-icons/boxicons-regular';
import BarLoader from 'react-spinners/BarLoader';
import Grid from '@material-ui/core/Grid';
import database from '../../../model/firebase/firebase';
import { am4charts, am4core } from '../../../system/utils/amcharts';
import DownloadVacanciesReport from './excel/vacancies';
import esMessages from '../../../system/utils/messagesSpanishKendo/es.json';
import { palette } from '../../../system/styles/styles';

function VacanciesDashboard(props) {
  const [state, setState] = React.useState({
    totalCompanies: 0,
    message: false,
    promVacancies: [],
    renderGraph: false,
    data: [],
    topFive: [],
    activeVacanciesTotal: [],
    atsActive: [],
    atsVacancies: [],
    generalVacancies: [],
    companies: [],
    filterValue: 'all',
    internVacancies: [],
    allTime: true,
    maxDate: null,
    minDate: null,
    openMenu: false,
    campus: '',
  });

  const vacancies = useSelector((state) => state.vacancies);
  const platform = useSelector((state) => state.platform);
  const companies = useSelector((state) => state.companies);

  const language = { locale: 'es' };
  loadMessages(esMessages, 'es'); // Le decimos que mensajes queremos por idioma
  const maxDate = new Date();

  const chart = React.useRef(null);
  const anchorEl = React.useRef(null);
  const contentRef = React.useRef();
  const blurTimeoutRef = React.useRef();

  const onOpen = (e) => {
    contentRef.current.focus();
  };

  const onFocus = () => {
    // the user is still inside the content
    clearTimeout(blurTimeoutRef.current);
  };

  const onBlurTimeout = () => {
    // the user is now outside the popup
    setState((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const onBlur = () => {
    clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(onBlurTimeout, 200);
  };

  const getAnalytics = async () => {
    const newgeneralVacancies = (
      await database.collection(`${platform.platformName}`).doc('data').collection('vacancies').get()
    ).docs.reduce(
      (acc, element) => {
        const vacancy = element.data();
        const evalDate = dayjs(vacancy.postedFrom).isBetween(state.minDate, state.maxDate);
        console.log(vacancy, state.allTime);
        if (state.allTime) {
          if (!!vacancy.carrer && Array.isArray(vacancy.carrer)) {
            vacancy.carrer = Array.isArray(vacancy.carrer) ? vacancy.carrer.toString() : vacancy.carrer;
          }
          if (vacancy.Permission === 'accept') {
            acc.generalVacancies.push(vacancy);
          }
          if (vacancy.Permission === 'accept' && vacancy.ATS === false) {
            acc.internVacancies.push(vacancy);
          }
          if (vacancy.Permission === 'accept' && vacancy.ATS === true) {
            acc.atsVacancies.push(vacancy);
          }
          if (vacancy.Status && vacancy.Permission === 'accept') {
            acc.activeVacanciesTotal.push(vacancy);
          }
        } else if (evalDate) {
          if (!!vacancy.carrer && Array.isArray(vacancy.carrer)) {
            vacancy.carrer = vacancy.carrer.map((a) => a.value);
          }
          if (vacancy.Permission === 'accept') {
            acc.generalVacancies.push(vacancy);
          }
          if (vacancy.Permission === 'accept' && vacancy.ATS === false) {
            acc.internVacancies.push(vacancy);
          }
          if (vacancy.Permission === 'accept' && vacancy.ATS === true) {
            acc.atsVacancies.push(vacancy);
          }
          if (vacancy.Status && vacancy.Permission === 'accept') {
            acc.activeVacanciesTotal.push(vacancy);
          }
        }
        return acc;
      },
      {
        generalVacancies: [],
        internVacancies: [],
        atsVacancies: [],
        activeVacanciesTotal: [],
      }
    );
    console.log(newgeneralVacancies);
    setState((prev) => ({
      ...prev,
      generalVacancies: newgeneralVacancies.generalVacancies,
      internVacancies: newgeneralVacancies.internVacancies,
      atsVacancies: newgeneralVacancies.atsVacancies,
      activeVacanciesTotal: newgeneralVacancies.activeVacanciesTotal,
    }));
    return {
      generalVacancies: newgeneralVacancies.generalVacancies,
      internVacancies: newgeneralVacancies.internVacancies,
      atsVacancies: newgeneralVacancies.atsVacancies,
      activeVacanciesTotal: newgeneralVacancies.activeVacanciesTotal,
    };
  };

  const updateAnalytics = async () => {
    setState((prev) => ({
      ...prev,
      companies: [],
      atsVacancies: [],
      generalVacancies: [],
      activeVacancies: [],
      activeVacanciesTotal: [],
      internVacancies: [],
      renderGraph: false,
    }));
    const newCompanies = companies.reduce((acc, element) => {
      if (element.active === true && state.allTime) {
        acc.push(element);
        return acc;
      }
      const evalDate = dayjs(element.authTime).isBetween(state.minDate, state.maxDate);
      if (element.active === true && evalDate) {
        acc.push(element);
        return acc;
      }
      return acc;
    }, []);
    setState((prev) => ({
      ...prev,
      companies: newCompanies,
    }));
    const data = await getAnalytics();
    const companiesLength = state.companies.length;
    const vacancies = data.internVacancies.length + data.atsVacancies.length;
    const activeVacancies = data.activeVacanciesTotal.length;
    const vacancyCount = data.internVacancies.length;
    const atsVacancies = data.atsVacancies.length;
    const prom = vacancyCount === 0 && companiesLength === 0 ? 0 : parseInt(vacancyCount / companiesLength);
    await setState((prev) => ({
      ...prev,
      data: [vacancies, vacancyCount, atsVacancies, activeVacancies, prom],
      renderGraph: true,
    }));
  };

  const handleClick = (event) => {
    setState((prev) => ({
      ...prev,
      open: !prev.open,
    }));
  };

  const handleCloseMenu = () => {
    setState((prev) => ({
      ...prev,
      anchorEl: null,
    }));
  };

  const changedRangeInputs = (e) => {
    const { end, start } = e.value;
    if (start) {
      setState((prev) => ({
        ...prev,
        minDate: start,
        allTime: false,
      }));
    }
    if (end) {
      setState((prev) => ({
        ...prev,
        maxDate: end,
        allTime: false,
      }));
    }
    if (start !== null && end !== null) {
      updateAnalytics(state.campus);
    }
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
    updateAnalytics();
  }, []);

  React.useLayoutEffect(() => {
    const x = am4core.create('barChartVacancies', am4charts.XYChart);

    x.data = [
      {
        category: 'Total publicadas',
        value: state.data[0] || 0,
      },
      {
        category: 'Total internas',
        value: state.data[1] || 0,
      },
      {
        category: 'Total externas',
        value: state.data[2] || 0,
      },
      {
        category: 'Total activas',
        value: state.data[3] || 0,
      },
      {
        category: 'Vacantes / Empresa',
        value: state.data[3] / companies.length || 0,
      },
    ];

    const categoryAxis = x.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'category';
    categoryAxis.title.text = '';
    categoryAxis.renderer.minGridDistance = 40;
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.cellStartLocation = 0.2;
    categoryAxis.renderer.cellEndLocation = 0.8;
    categoryAxis.renderer.minGridDistance = 30;

    const valueAxis = x.yAxes.push(new am4charts.ValueAxis());
    valueAxis.title.text = '';

    // Otra serie
    const series4 = x.series.push(new am4charts.ColumnSeries());
    series4.name = '{category}';
    series4.stroke = am4core.color(`${platform.adminColor ? platform.adminColor : '#0f3f44'}`);
    series4.strokeWidth = 3;
    series4.dataFields.valueY = 'value';
    series4.tooltipText = `{category}: [bold]{valueY}[/]`;
    series4.dataFields.categoryX = 'category';
    series4.smoothing = 'monotoneX';
    series4.columns.template.fill = am4core.color(`${platform.adminColor ? platform.adminColor : '#0f3f44'}`); // fill
    series4.columns.template.width = am4core.percent(60);

    // Cambia el color del background del tootip
    series4.tooltip.getFillFromObject = false;
    series4.tooltip.background.fill = am4core.color('#FFF');

    // Set text color
    series4.tooltip.label.fill = am4core.color('#000');

    const scrollbarX = new am4charts.XYChartScrollbar();
    scrollbarX.series.push(series4);
    scrollbarX.start = 0;
    scrollbarX.end = 0.08;
    x.scrollbarX = scrollbarX;

    // Add cursor
    x.cursor = new am4charts.XYCursor();

    chart.current = x;

    return () => {
      x.dispose();
    };
  }, [state.renderGraph]);

  if (!state.renderGraph)
    return (
      <div
        className={props.className}
        style={{
          height: '500px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '30px 46px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#fff',
        }}>
        <BarLoader sizeUnit="px" size={250} color={palette.blue} />
      </div>
    );

  return (
    <Grid container spacing={2} className={props.className}>
      <Grid item lg={11} className="dash-card">
        <div className="card-heading">
          <h2 className="card-title">Vacante Insights</h2>
          <div className="dashboard-date-container">
            <DateRangePicker
              format="dd-MMM-yyyy"
              value={{ start: state.minDate, end: state.maxDate }}
              onChange={changedRangeInputs}
              max={maxDate}
            />
          </div>
          <button type="button" onClick={handleClick} ref={anchorEl} style={{ background: 'transparent' }}>
            <DotsVerticalRounded size={20} color="#232323" />
          </button>
          <Popup anchor={anchorEl.current} show={state.open} onOpen={onOpen} popupClass="popup-content">
            <div ref={contentRef} tabIndex={0} onFocus={onFocus} onBlur={onBlur}>
              <button type="button" onClick={handleCloseMenu}>
                <DownloadVacanciesReport
                  filename={`ResumenCampus-${state.campus}`}
                  companiesDataSet={state.generalVacancies}
                />
              </button>
            </div>
          </Popup>
        </div>
        <div className="analytics-container">
          <div className="dashboard-card">
            <div className="vacancy-graph-container">
              <div className="graph-filter-container" />
              <div className="graph-container" id="barChartVacancies" />
            </div>
          </div>
        </div>
      </Grid>

      {/* <Grid item lg={4} className="dash-card"> */}
      {/*  <div className="card-heading"> */}
      {/*    <h2 className="card-title">Datos</h2> */}
      {/*  </div> */}
      {/*  <div className="graph-full-container-vacancy"> */}
      {/*    <div className="minicards-container"> */}
      {/*      <div className="minicard-resume"> */}
      {/*        <ShowChart size={24} className="decorative-icon-a" /> */}
      {/*        <p className="minicard-d-result">{state.data[0]}</p> */}
      {/*        <p className="minicard-d-title">Total de vacantes publicadas</p> */}
      {/*      </div> */}
      {/*      <div className="minicard-resume"> */}
      {/*        <ShowChart size={24} className="decorative-icon-a" /> */}
      {/*        <p className="minicard-d-result">{state.data[1]}</p> */}
      {/*        <p className="minicard-d-title">Vacantes internas</p> */}
      {/*      </div> */}
      {/*      <div className="minicard-resume"> */}
      {/*        <ShowChart size={24} className="decorative-icon-a" /> */}
      {/*        <p className="minicard-d-result">{state.data[2]}</p> */}
      {/*        <p className="minicard-d-title">Vacantes externas</p> */}
      {/*      </div> */}
      {/*      <div className="minicard-resume"> */}
      {/*        <ShowChart size={24} className="decorative-icon-a" /> */}
      {/*        <p className="minicard-d-result">{state.data[3]}</p> */}
      {/*        <p className="minicard-d-title">Vacantes activas</p> */}
      {/*      </div> */}
      {/*      <div className="minicard-resume"> */}
      {/*        <ShowChart size={24} className="decorative-icon-a" /> */}
      {/*        <p className="minicard-d-result">{state.data[4]}</p> */}
      {/*        <p className="minicard-d-title">Vacantes / Empresa</p> */}
      {/*      </div> */}
      {/*    </div> */}
      {/*  </div> */}
      {/* </Grid> */}
    </Grid>
  );
}

export default styled(VacanciesDashboard)`
  width: 100%;
  font-family: 'Sofia Pro', sans-serif;
  margin-top: 64px;
  padding: 0 32px;
  .dash-card {
    background-color: #fff;
    width: 100%;
    border-radius: 8px;
    margin: 16px;
  }
  .card-heading {
    background-color: #fff;
    border-bottom: solid 2px #f9f9f9;
    width: 100%;
    padding: 12px;
    display: grid;
    grid-template-columns: 2fr 3fr 1fr;
    margin-bottom: 12px;
    min-height: 50px;
  }
  .card-title {
    color: #0f3f44;
    font-family: 'Cooper Md BT', serif;
    font-size: 16px;
    align-self: center;
  }
  .top-five-item {
    background-color: #859a9d;
    display: grid;
    grid-template-columns: 8fr 2fr;
    margin: 12px 0;
    border-radius: 8px;
    padding: 12px;
    p,
    .top-five-text,
    .top-five-count {
      color: #fff;
    }
    p {
      margin: 0 !important;
      color: #fff !important;
      font-size: 12px !important;
    }
    .top-five-text {
      font-family: 'Cooper Md BT', serif;
      font-size: 20px;
      text-transform: capitalize;
    }
    .top-five-count {
      color: #fff;
      font-size: 24px;
      align-content: center;
      align-self: center;
      align-items: center;
      margin-bottom: 8px;
      font-weight: 700;
      justify-self: center;
    }
  }
  label {
    margin-left: 16px;
    color: #26484e;
  }
  select {
    padding: 8px 12px 8px 32px;
    border-radius: 8px;
    margin-left: 8px;
  }
  .analytics-loader-show {
    background-color: var(--white-color);
    width: 100%;
    padding: 0px;
    position: relative;
    top: 0px;
    z-index: 9999;
    width: 100%;
    height: 90vh;
    padding-right: 0px;
    margin-right: -36px;
    padding-left: 0px;
  }
  .analytics-loader-hide {
    position: fixed;
    left: 9999px;
  }
  .k-label {
    margin-left: 0;
  }
  .k-input {
    font-family: 'Sofia Pro', sans-serif;
  }
  .k-button {
    background: #0f3f44;
    border-color: #0f3f44;
  }

  .graph-filter-container {
    width: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-left: auto;
    padding-top: 8px;
  }
  .graph-container {
    width: 100%;
    display: block;
    margin-left: auto;
    margin-right: auto;
    border-radius: 5px;
    padding: 10px;
    height: 500px;
  }
  //.dashboard-navbar {
  //  width: 100%;
  //  display: flex;
  //  justify-content: space-between;
  //  align-items: center;
  //  margin-bottom: 42px;
  //  p {
  //    font-size: 2rem;
  //    font-weight: 500;
  //    line-height: 32px;
  //    width: auto;
  //    display: block;
  //    color: var(--text-black);
  //    margin: 0px;
  //  }
  //}
  //.dashboard-date-container {
  //  display: flex;
  //  justify-content: center;
  //  align-items: center;
  //}
  //.dashboard-date-box {
  //  border: solid 2px #edeef9;
  //  padding: 4px;
  //}
  //.analytics-container-hide {
  //  width: 100%;
  //  border: none;
  //  position: fixed;
  //  left: 9999px;
  //}
  //.graph-full-container-vacancy {
  //  flex-direction: row;
  //  width: 100%;
  //  display: flex;
  //  flex-wrap: wrap;
  //  justify-content: space-between;
  //}
  //.vacancy-graph-container {
  //  width: 60%;
  //}

  //.minicards-container {
  //  width: 35%;
  //  display: flex;
  //  flex-wrap: wrap;
  //  align-items: flex-start;
  //  justify-content: flex-start;
  //  margin-top: 32px;
  //}
  //.minicard-resume {
  //  display: flex;
  //  /* padding: 10px; */
  //  flex-direction: column;
  //  font-size: 1.7rem;
  //  width: 33.333%;
  //  align-items: center;
  //  justify-content: center;
  //}
  //.minicard-d-result {
  //  font-size: 1.4rem;
  //  color: #aeaec1;
  //  font-weight: 500;
  //  line-height: 32px;
  //  width: 100%;
  //  text-align: center;
  //  margin-bottom: 4px;
  //}
  //.minicard-d-title {
  //  font-size: 1rem;
  //  width: 100%;
  //  text-align: center;
  //  color: #aeaec1;
  //  font-weight: 500;
  //}
`;
